import classNames from "classnames";
import type React from "react";
import { type MouseEventHandler, useEffect, useRef, useState } from "react";
import "./HandleDragAndDrop.css";

type Props = {
    label: string;
    children: React.ReactNode;
    className?: string;
    onClick?: MouseEventHandler<HTMLDivElement>;
    onDropFiles: (files: FileList) => void;
    onDropData?: (data: any) => void;
};

const HandleDragAndDrop = function HandleDragAndDrop({
    label,
    children,
    className,
    onClick,
    onDropFiles,
    onDropData,
}: Props) {
    const dragAndDropRef = useRef<HTMLDivElement>(null);
    const [isDragging, setIsDragging] = useState(false);

    useEffect(() => {
        const handleDragIn = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
        };
        const handleDragOut = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);
        };
        const handleDrag = (e: MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(true);
        };
        const handleDrop = (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            setIsDragging(false);

            const files = e.dataTransfer?.files;
            if (files && files.length > 0 && onDropFiles) {
                onDropFiles(files);
            }

            const json = e.dataTransfer?.getData("application/json");
            if (json && onDropData) {
                const data = JSON.parse(json);
                onDropData(data);
            }
        };

        const reference = dragAndDropRef.current;
        reference?.addEventListener("dragenter", handleDragIn);
        reference?.addEventListener("dragleave", handleDragOut);
        reference?.addEventListener("dragover", handleDrag);
        reference?.addEventListener("drop", handleDrop);

        return () => {
            reference?.removeEventListener("dragenter", handleDragIn);
            reference?.removeEventListener("dragleave", handleDragOut);
            reference?.removeEventListener("dragover", handleDrag);
            reference?.removeEventListener("drop", handleDrop);
        };
    }, [onDropFiles, onDropData]);

    return (
        <div
            ref={dragAndDropRef}
            onClick={onClick}
            role="button"
            tabIndex={0}
            data-drag-and-drop={label}
            className={classNames(className, "drag-and-drop", {
                "drag-and-drop-active": isDragging,
            })}
        >
            {children}
        </div>
    );
};

export default HandleDragAndDrop;
